import { signUpCache } from "@faire/retailer-visitor-shared/features/SignUp/SignUpRequestData";
import { PersonalInfoData } from "@faire/retailer-visitor-shared/features/SignUp/SignUpRequestData";
import { useRetailer } from "@faire/retailer-visitor-shared/hooks/retailer/useRetailer";
import { assignWithLocale } from "@faire/retailer-visitor-shared/lib/assignWithLocale";
import { useSignUpQueryParams } from "@faire/retailer-visitor-shared/views/SignUp/_hooks/useSignUpQueryParams";
import { saveCredentialsInBrowser } from "@faire/retailer-visitor-shared/views/SignUp/_utils/credentialsUtils";
import { getLocationOrThrow } from "@faire/web--source/common/globals/getLocation";
import { useEffectEvent } from "@faire/web--source/ui/hooks/useEffectEvent";
import { useQuery as useInvitationQuery } from "@faire/web-api--source/endpoints/www/api/retailer/invitations/invitationToken/get-hooks";
import { useMutation as useOnboardSubUserMutation } from "@faire/web-api--source/endpoints/www/api/retailer/invitations/onboard/post-hooks";
import { trackUserInvitationSubuserOnboardingPageAccountCreated } from "@faire/web-api--source/events/userInvitation/actionCategoryOther/subuserOnboardingPageAccountCreated";
import { trackUserInvitationSubuserOnboardingPageCreateAccountClick } from "@faire/web-api--source/events/userInvitation/click/subuserOnboardingPageCreateAccount";
import { trackUserInvitationSignUpAsNewUserSubmit } from "@faire/web-api--source/events/userInvitation/submit/signUpAsNewUser";
import { trackUserInvitationSignUpAsNewUserView } from "@faire/web-api--source/events/userInvitation/view/signUpAsNewUser";
import { trackUserInvitationSubuserOnboardingPageView } from "@faire/web-api--source/events/userInvitation/view/subuserOnboardingPage";
import { IOnboardUserRequest } from "@faire/web-api--source/indigofair/data/IOnboardUserRequest";
import { IOnboardUserResponse } from "@faire/web-api--source/indigofair/data/IOnboardUserResponse";
import { IUser } from "@faire/web-api--source/indigofair/data/IUser";
import homepage from "@faire/web-api--source/routes/www/root";
import { useCallback, useEffect } from "react";

export const useTrackRetailerSubUserView = () => {
  const { isRetailerSubUser, invitationToken } = useRetailerSubUser();
  const trackSubUserView = useEffectEvent(() => {
    if (isRetailerSubUser) {
      trackUserInvitationSubuserOnboardingPageView();
      trackUserInvitationSignUpAsNewUserView(invitationToken ?? "");
    }
  });

  useEffect(() => {
    trackSubUserView();
  }, [trackSubUserView]);
};

export const useRetailerSubUser = () => {
  const { queryParams, isRetailerSubUser } = useSignUpQueryParams();
  const invitationToken = queryParams.ifuit;

  const {
    data: invitation,
    isLoading: isLoadingInvitation,
    error: invitationError,
  } = useInvitationQuery(invitationToken ?? "", {
    enabled: isRetailerSubUser && !!invitationToken,
    staleTime: 5 * 60 * 1000,
  });

  return {
    isRetailerSubUser,
    invitationToken,
    invitation,
    isLoadingInvitation,
    invitationError,
  };
};

export const useSignupRetailerSubUser = () => {
  const {
    isPending: isSigningUp,
    error: signUpError,
    mutate: onboardSubUser,
  } = useOnboardSubUserMutation();
  const { setRetailer } = useRetailer();
  const { invitationToken } = useRetailerSubUser();
  const token = invitationToken ?? "";

  const handlePostSignUp = useCallback(
    (
      { retailer, user, mobile_magic_login_link }: IOnboardUserResponse,
      request: IOnboardUserRequest
    ) => {
      trackUserInvitationSubuserOnboardingPageAccountCreated(
        retailer?.token ?? "",
        user?.token ?? ""
      );

      setRetailer(retailer);
      signUpCache.clear();

      const location = getLocationOrThrow();
      if (mobile_magic_login_link) {
        location.assign(mobile_magic_login_link);
        return;
      }

      saveCredentialsInBrowser(
        request.user?.email_address,
        request.user?.password
      );
      assignWithLocale(homepage());
    },
    [setRetailer]
  );

  const signUpSubUser = useCallback(
    async (data: PersonalInfoData) => {
      trackUserInvitationSubuserOnboardingPageCreateAccountClick();
      trackUserInvitationSignUpAsNewUserSubmit(token);

      const payload = IOnboardUserRequest.build({
        user: IUser.build({ ...data }),
        invitation_token: token,
      });

      onboardSubUser([payload], {
        onSuccess: (response, variables) =>
          handlePostSignUp(response, variables[0]),
      });
    },
    [handlePostSignUp, onboardSubUser, token]
  );

  return { signUpSubUser, isSigningUp, signUpError };
};
